// @flow

import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';
import { normalize } from 'normalizr';
import type {
  typeBundleType,
  reduxAction,
  actionBundleNestedManyType,
  actionBundleNestedSingleType,
  endpointNestedType,
  selectorNestedSetType,
} from '../dataStoreTypes';
import { selectSession } from '../reducers/session';
import { mapEntitiesToHaveSessionId } from '../utils';

export default function* genericNestedFetcherSagas(
  selectorSet: selectorNestedSetType<*>,
  actionBundleMany: actionBundleNestedManyType,
  actionBundleSingle: actionBundleNestedSingleType,
  typeBundleMany: typeBundleType,
  typeBundleSingle: typeBundleType,
  endpoint: endpointNestedType,
  schema: any,
): Generator<any, void, any> {
  const sagas = {
    *getManyIfNeeded(action: reduxAction) {
      const { parentId } = action;
      const things = yield select(selectorSet.metaMany(parentId));
      const sessionId = yield select(selectSession.sessionId);

      if (
        !things ||
        (things.sessionId < sessionId &&
          things.status !== 1000 &&
          things.deduplicationCounter === 1)
      ) {
        yield put(actionBundleMany.pending(parentId));
      }
    },
    *getMany(action: reduxAction) {
      const { parentId } = action;
      const sessionId = yield select(selectSession.sessionId);

      try {
        const manyThings = yield call(endpoint.getMany, parentId);
        const { results } = manyThings.body;

        const { result, entities } = normalize(results, [schema]);

        const mappedEntities = mapEntitiesToHaveSessionId(sessionId, entities);

        yield put(
          actionBundleMany.success(parentId, result, mappedEntities, sessionId),
        );
      } catch (e) {
        yield put(actionBundleMany.error(parentId, e.status, e.body));
      }
    },
    *getSingleIfNeeded(action: reduxAction) {
      const { parentId, id } = action;
      const thing = yield select(selectorSet.singleById(parentId, id));
      const sessionId = yield select(selectSession.sessionId);
      const loadingStatus = yield select(
        selectorSet.metaSingleById(parentId, id),
      );

      if (
        !loadingStatus ||
        (loadingStatus.status !== 1000 &&
          (!thing ||
            (thing.sessionId < sessionId && thing.deduplicationCounter === 1)))
      ) {
        yield put(actionBundleSingle.pending(parentId, id));
      }
    },
    *getSingle(action: reduxAction) {
      const { parentId, id } = action;
      const sessionId = yield select(selectSession.sessionId);

      try {
        const thing = yield call(endpoint.getSingle, parentId, id);
        const { result, entities } = normalize(thing.body, schema);

        const mappedEntities = mapEntitiesToHaveSessionId(sessionId, entities);

        yield put(
          actionBundleSingle.success(
            parentId,
            result,
            [result],
            mappedEntities,
          ),
        );
      } catch (e) {
        yield put(actionBundleSingle.error(parentId, id, e.status, e.body));
      }
    },
    *setSingleDirect(action: reduxAction) {
      const { parentId, id } = action;
      const sessionId = yield select(selectSession.sessionId);

      try {
        const thing = action.apiResponse;
        const { result, entities } = normalize(thing.body, schema);
        const mappedEntities = mapEntitiesToHaveSessionId(sessionId, entities);

        yield put(
          actionBundleSingle.success(
            parentId,
            result,
            [result],
            mappedEntities,
          ),
        );
      } catch (e) {
        // Should never reach here, but just in case...
        yield put(actionBundleSingle.error(parentId, id, 0, {}));
      }
    },
    *setManyDirect(action: reduxAction) {
      const { parentId } = action;
      const sessionId = yield select(selectSession.sessionId);

      try {
        const thing = action.apiResponse;
        const { result, entities } = normalize(thing.body, schema);
        const mappedEntities = mapEntitiesToHaveSessionId(sessionId, entities);

        yield put(
          actionBundleMany.success(parentId, result, mappedEntities, sessionId),
        );
      } catch (e) {
        // Should never reach here, but just in case...
        yield put(actionBundleMany.error(parentId, 0, {}));
      }
    },
  };

  yield all([
    takeLeading(typeBundleMany.IFNEEDED, sagas.getManyIfNeeded),
    takeEvery(typeBundleMany.PENDING, sagas.getMany),
    takeEvery(typeBundleMany.SHORTDIRECT, sagas.setManyDirect),
    takeLeading(typeBundleSingle.IFNEEDED, sagas.getSingleIfNeeded),
    takeEvery(typeBundleSingle.PENDING, sagas.getSingle),
    takeEvery(typeBundleSingle.SHORTDIRECT, sagas.setSingleDirect),
  ]);
}
